// Limites de caracteres documentados oficialmente por cada plataforma de
// anúncios, usados tanto para exibir os contadores (`CharCounter`) quanto
// para bloquear o avanço de etapa (`canGoNext` em AdsContext) quando um
// campo os ultrapassa. Centralizado aqui para os dois lados ficarem
// sempre em sincronia — mudar um número aqui já reflete no bloqueio.
//
// Cada campo pode ter dois números:
// - `recommended`: limite de exibição sugerido pela plataforma — acima
//   disso o texto ainda é aceito, só é truncado (ex: "ver mais" no feed).
//   NÃO bloqueia o avanço — só gera um aviso visual (contador âmbar).
// - `hard`: limite técnico real, imposto pela API/formulário — a
//   plataforma rejeita ou corta o texto se ultrapassado. Esse é o único
//   valor que bloqueia o avanço de etapa.
// Quando só um dos dois é conhecido com confiança, o outro fica ausente.

export interface CharLimit {
  recommended?: number;
  hard?: number;
}

// Nome da campanha (Etapa 1) — limite técnico (hard) de cada plataforma.
// Meta não documenta publicamente um limite específico para o nome da
// campanha (só ~400 para o nome do conjunto de anúncios) — usa-se o
// mesmo valor de Google Ads (128) como aproximação conservadora, por
// decisão do usuário.
export const CAMPAIGN_NAME_LIMITS: Record<string, number> = {
  meta: 128,
  google_ads: 128,
  linkedin: 255,
  tiktok: 512,
};

// Nome da empresa exibido nos anúncios do Google Ads (Etapa 1) — hard.
export const GOOGLE_ADS_BUSINESS_NAME_LIMIT = 25;

// Copy/Headline/Description do criativo (Etapa 2).
export const AD_TEXT_LIMITS: Record<
  string,
  { message?: CharLimit; headline?: CharLimit; description?: CharLimit }
> = {
  meta: {
    // Primary Text: recomendado 125 (trunca com "ver mais" no feed),
    // limite técnico da API ~2.200 caracteres.
    message: { recommended: 125, hard: 2200 },
    // Headline: recomendado 40 para exibição, limite técnico ~255.
    headline: { recommended: 40, hard: 255 },
    // Description (link description): recomendado 30. Não há uma fonte
    // oficial confirmando um limite técnico distinto — fica sem `hard`
    // para não bloquear com base num número não confirmado.
    description: { recommended: 30 },
  },
  linkedin: {
    // Introductory Text: recomendado 150 para evitar truncamento,
    // máximo técnico 255 (confirmado na página oficial da LinkedIn).
    message: { recommended: 150, hard: 255 },
    // Headline (Sponsored Content): 70 é o próprio limite documentado
    // oficialmente pela LinkedIn — não há um número "recomendado"
    // separado, então recommended = hard.
    headline: { recommended: 70, hard: 70 },
    // Description — só usado no LinkedIn Audience Network (LAN),
    // documentado oficialmente como 70 caracteres.
    description: { recommended: 70, hard: 70 },
  },
  tiktok: {
    // Ad Text: TikTok documenta 100 caracteres como limite técnico
    // (não aceita mais que isso), recomendando até 60 para não truncar.
    message: { recommended: 60, hard: 100 },
  },
};

// Formulário de Leads nativo do LinkedIn (Etapa 4) — todos hard (LinkedIn
// rejeita no envio se ultrapassar).
export const LINKEDIN_LEAD_FORM_LIMITS = {
  formName: 256,
  headline: 60,
  description: 160,
};

// Rótulo de pergunta customizada do formulário de leads (Etapa 4) — regra
// própria da aplicação (LinkedIn permite até 100/pergunta oficialmente).
export const CUSTOM_QUESTION_LABEL_LIMIT = 50;

// Mensagem de aviso (não bloqueante) para quando o texto passou do
// `recommended` mas ainda está dentro do `hard` — usada como `warning` em
// Input/Textarea, junto com o contador ficando âmbar. Quando não há
// `recommended`, ou o texto já ultrapassou o `hard` (nesse caso o aviso
// vermelho de bloqueio do banner de avanço já cobre o alerta), retorna
// undefined — não duplica mensagem.
export function getRecommendedLimitWarning(
  current: number,
  limit: CharLimit | undefined,
  t: (key: string) => string,
): string | undefined {
  if (!limit?.recommended || current <= limit.recommended) return undefined;
  if (limit.hard && current > limit.hard) return undefined;
  return t("Acima do recomendado, pode ser truncado.");
}
